import { useState } from 'react';
import { toHiragana, toRomaji } from 'wanakana';

export default function FlashcardReview({ words, onClose }) {
  const [cards] = useState(() => shuffle([...words]));
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [known, setKnown] = useState(0);
  const [learning, setLearning] = useState(0);

  const card = cards[idx];
  const done = idx >= cards.length;

  function answer(isKnown) {
    if (isKnown) setKnown(k => k + 1);
    else setLearning(l => l + 1);
    setRevealed(false);
    setIdx(i => i + 1);
  }

  if (done) {
    return (
      <Overlay onClose={onClose}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700 }}>Review complete!</h2>
          <p style={{ color: '#888', margin: '0 0 28px', fontSize: 14 }}>{cards.length} cards reviewed</p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginBottom: 32 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#4caf50' }}>{known}</div>
              <div style={{ fontSize: 13, color: '#888' }}>Know it</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#f44336' }}>{learning}</div>
              <div style={{ fontSize: 13, color: '#888' }}>Still learning</div>
            </div>
          </div>
          <button onClick={onClose} style={btnStyle('#333', true)}>Done</button>
        </div>
      </Overlay>
    );
  }

  const reading = card.reading ? toHiragana(card.reading) : '';
  const romaji = card.reading ? toRomaji(card.reading) : '';

  return (
    <Overlay onClose={onClose}>
      {/* Progress bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#aaa', marginBottom: 6 }}>
          <span>{idx + 1} / {cards.length}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, padding: '10px', margin: '-10px', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ height: 4, background: '#f0ede8', borderRadius: 2 }}>
          <div style={{
            height: '100%', background: '#4a90d9', borderRadius: 2,
            width: `${(idx / cards.length) * 100}%`, transition: 'width 0.3s',
          }} />
        </div>
      </div>

      {/* Card face */}
      <div style={{
        background: 'white', borderRadius: 16, padding: '36px 28px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center',
        marginBottom: 20, minHeight: 180,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 60, fontWeight: 700, fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif', lineHeight: 1.1, marginBottom: 10 }}>
          {card.word}
        </div>
        {card.jlpt && (
          <span style={{ ...jlptBadge(card.jlpt), marginBottom: 12 }}>{card.jlpt}</span>
        )}

        {revealed ? (
          <div style={{ width: '100%', marginTop: 8 }}>
            {(reading || romaji) && (
              <div style={{ fontSize: 15, color: '#666', marginBottom: 10 }}>
                {reading}{reading && romaji && <span style={{ color: '#ccc', margin: '0 6px' }}>·</span>}{romaji}
              </div>
            )}
            {card.pos && (
              <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{card.pos}</div>
            )}
            <ol style={{ margin: 0, paddingLeft: 20, textAlign: 'left' }}>
              {card.definitions.slice(0, 3).map((d, i) => (
                <li key={i} style={{ fontSize: 14, color: '#333', marginBottom: 4, lineHeight: 1.5 }}>{d}</li>
              ))}
            </ol>
            {card.sentence && (
              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: '#f7f5f0', borderRadius: 8,
                fontSize: 13, color: '#555', lineHeight: 1.9, textAlign: 'left',
                fontFamily: '"Hiragino Mincho ProN", serif',
              }}>
                {card.sentence}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setRevealed(true)}
            style={{ ...btnStyle('#4a90d9'), marginTop: 8, minWidth: 140 }}
          >
            Show answer
          </button>
        )}
      </div>

      {/* Answer buttons */}
      {revealed && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => answer(false)} style={{ ...btnStyle('#f44336'), flex: 1 }}>
            ✗ Still learning
          </button>
          <button onClick={() => answer(true)} style={{ ...btnStyle('#4caf50'), flex: 1 }}>
            ✓ Know it
          </button>
        </div>
      )}
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.45)' }} />
      <div style={{
        position: 'fixed', zIndex: 1400,
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 460px)',
        padding: '28px 24px',
        background: '#faf9f6',
        borderRadius: 20,
        boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
      }}>
        {children}
      </div>
    </>
  );
}

function jlptBadge(jlpt) {
  const colors = { N5: '#4caf50', N4: '#8bc34a', N3: '#ff9800', N2: '#ff5722', N1: '#e53935' };
  return {
    display: 'inline-block', fontSize: 10, fontWeight: 700,
    padding: '2px 7px', borderRadius: 4,
    background: colors[jlpt] || '#999', color: 'white', letterSpacing: 0.5,
  };
}

function btnStyle(color, wide = false) {
  return {
    padding: wide ? '10px 36px' : '10px 16px',
    fontSize: 14, fontWeight: 600,
    background: color, color: 'white',
    border: 'none', borderRadius: 10, cursor: 'pointer',
  };
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
